'use client';

import { c as _c } from "react/compiler-runtime";
import { isSdkError, useGenerateImage, useGetImagesQuota } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { GeneratingFrame } from '@/components/images/generating-frame';
import { ImageShimmer, OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Alert, AlertDescription } from '@/components/ui/app-alert';
import { RepeatIcon, SparklesIcon } from '@/components/ui/icon/glyphs';
import { TextareaField } from '@/components/ui/textarea-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import type { ImageGenerationFailureReason } from '@/events/image-generation';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
export interface MorfianTabProps {
  /** Pre-fills "Describe yourself" — typically the user's display name. */
  initialDescription?: string;
  /** Stored seed from `users.avatarPreferences.morfianSeed`. Drives deterministic re-rolls. */
  storedSeed?: string;
  /** Fired when the user confirms a generated Morfian. */
  onConfirm: (result: {
    ipfsUrl: string;
    seed: string;
  }) => void;
  /** True only while the gate-enabled Morfian picker is open and active. */
  quotaEnabled: boolean;
  /** Optional cancel — used when the tab is mounted inside a parent wizard. */
  onCancel?: () => void;
}
type State = {
  kind: 'idle';
} | {
  kind: 'generating';
} | {
  kind: 'result';
  imageUrl: string;
  seed: string;
} | {
  kind: 'error';
  reason: ImageGenerationFailureReason;
};
const MAX_DESCRIPTION_LENGTH = 200;

/**
 * Hash a UUID-like string into a 32-bit unsigned integer for use as a fal.ai
 * `seed`. Same semantics as `hashStringToSeed` in pattern-studio but the
 * pattern-studio helper hashes for palette derivation; this one feeds an
 * external API that expects an int. Deterministic so callers can persist
 * `seed` and reproduce the same Morfian.
 */
function hashSeedString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = hash * 31 + input.charCodeAt(i) >>> 0;
  }
  return hash;
}
function freshSeed(): string {
  if (typeof crypto !== 'undefined') return crypto.randomUUID(); // onClick handler / lazy initialiser only — not render
  return `${String(Date.now())}-${String(Math.random())}`; // onClick handler / lazy initialiser only — not render
}

// ALLOW-UNKNOWN: TanStack onError carries any thrown value — narrowed via the v2 SdkError type guard.
function classifyError(err: unknown): ImageGenerationFailureReason {
  if (err instanceof DOMException && err.name === 'AbortError') return 'unknown';
  if (isSdkError(err)) {
    // The backend image routes return a non-canonical failure body
    // `{ reason: 'nsfw' | 'quota_exceeded' | 'model' | 'invalid' |
    // 'unconfigured' | 'aborted', detail? }` (see
    // backend/backend-api-service/src/features/content/images/morfianate/route.ts
    // failureSchema). The raw body is attached to the SdkError as `body`
    // by throwHttpError; parse it here to recover the v1 distinction
    // between NSFW and generic validation rejects.
    const reason = extractFailureReason(err);
    if (reason === 'nsfw') return 'nsfw';
    if (reason === 'quota_exceeded') return 'quota_exceeded';
    // Treat HTTP 429 (`category: 'rate-limited'`) as quota-exhausted for
    // the Morfian avatar route — the user-facing surface for that route
    // is "you ran out", not "we throttled you".
    if (err.category === 'rate-limited') return 'quota_exceeded';
    if (err.category === 'server') return 'model';
    if (err.category === 'network') return 'network';
    return 'unknown';
  }
  if (err instanceof TypeError) return 'network';
  return 'unknown';
}

// the failure body is parsed from a raw response string;
// shape-checked below before any field access.
// ALLOW-UNKNOWN: mutation error body is opaque (open SDK error shape).
function extractFailureReason(err: unknown): string | undefined {
  // ALLOW-UNKNOWN: speculative `.body` field on a thrown mutation error — open shape.
  const body = (err as {
    body?: unknown;
  }).body;
  if (typeof body !== 'string' || body.length === 0) return undefined;
  // ALLOW-UNKNOWN: JSON.parse result is structurally unknown until shape-checked.
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    // SILENT-CATCH: non-JSON error body falls through to the generic toast message.
    return undefined;
  }
  if (parsed === null || typeof parsed !== 'object') return undefined;
  // ALLOW-UNKNOWN: speculative `.reason` field on the parsed JSON — open shape.
  const reason = (parsed as {
    reason?: unknown;
  }).reason;
  return typeof reason === 'string' ? reason : undefined;
}

/**
 * Self-contained Morfian generation tab — describe yourself → fal.ai/imagen4
 * (text → Pixar character) → Confirm. The backend pins to IPFS, so the URL
 * the modal hands back is the canonical avatar URL with no second upload step.
 *
 * State machine:
 *   idle → generating → result (preview + Re-roll + Confirm)
 *                     ↘ error (Try again) → idle
 */
export function MorfianTab(t0) {
  const $ = _c(96);
  const {
    initialDescription,
    storedSeed,
    onConfirm,
    quotaEnabled,
    onCancel
  } = t0;
  const t = useTranslations("avatar_picker");
  const tImageUpload = useTranslations("image_upload");
  const [description, setDescription] = useState(initialDescription ?? "");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "idle"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  const resultImageUrl = state.kind === "result" ? state.imageUrl : null;
  const resultNsfw = useNsfwStatusByUrl(resultImageUrl);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const abortControllerRef = useRef(null);
  const generationStartRef = useRef(0);
  let t2;
  if ($[1] !== storedSeed) {
    t2 = storedSeed ?? freshSeed();
    $[1] = storedSeed;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const pendingSeedRef = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      fields: ["used", "daily", "remaining", "resetsAt"]
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== quotaEnabled) {
    t4 = {
      enabled: quotaEnabled
    };
    $[4] = quotaEnabled;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const quota = useGetImagesQuota(t3, t4);
  let t5;
  if ($[6] !== description) {
    t5 = description.trim();
    $[6] = description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const trimmedDescription = t5;
  const remaining = quota.data?.remaining ?? quota.data?.daily ?? null;
  const dailyCap = quota.data?.daily ?? null;
  const quotaBlocks = remaining !== null && remaining <= 0;
  const canGenerate = trimmedDescription.length > 0 && !quotaExceeded && !quotaBlocks;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function abortInFlight() {
      if (abortControllerRef.current !== null) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const abortInFlight = t6;
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (abortControllerRef.current !== null) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    t8 = [];
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  const generateMutation = useGenerateImage();
  let t9;
  if ($[11] !== generateMutation || $[12] !== quota || $[13] !== t || $[14] !== tImageUpload || $[15] !== trimmedDescription) {
    t9 = function runGeneration(seed) {
      abortInFlight();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      generationStartRef.current = Date.now();
      setState({
        kind: "generating"
      });
      onEvent("image_generation_started", {
        mode: "morfian",
        hasUserPrompt: trimmedDescription.length > 0
      });
      showToast({
        id: TOAST_IDS.IMAGE_UPLOAD,
        message: tImageUpload("generating"),
        variant: "loading"
      });
      generateMutation.mutate({
        mode: "morfian",
        description: trimmedDescription,
        seed: hashSeedString(seed)
      }, {
        onSuccess: raw => {
          const data = raw as {
            imageUrl: string;
            promptUsed: string;
          };
          if (abortControllerRef.current !== controller) {
            return;
          }
          abortControllerRef.current = null;
          setState({
            kind: "result",
            imageUrl: data.imageUrl,
            seed
          });
          quota.refetch();
          const latencyMs = Date.now() - generationStartRef.current;
          onEvent("image_generation_completed", {
            mode: "morfian",
            latencyMs,
            hasUserPrompt: trimmedDescription.length > 0
          });
          showToast({
            id: TOAST_IDS.IMAGE_UPLOAD,
            message: tImageUpload("morfian_success"),
            variant: "success"
          });
        },
        onError: err => {
          if (err instanceof DOMException && err.name === "AbortError") {
            if (abortControllerRef.current === controller) {
              abortControllerRef.current = null;
            }
            return;
          }
          if (abortControllerRef.current !== controller) {
            return;
          }
          abortControllerRef.current = null;
          const reason = classifyError(err);
          logger.error("morfian generate failed", {
            error: err instanceof Error ? err.message.slice(0, 200) : String(err),
            reason
          });
          setState({
            kind: "error",
            reason
          });
          if (reason === "quota_exceeded") {
            setQuotaExceeded(true);
          }
          onEvent("image_generation_failed", {
            mode: "morfian",
            reason
          });
          const errorKey = reason === "quota_exceeded" ? "morfian_quota_exceeded" : reason === "nsfw" ? "morfian_error_nsfw" : reason === "network" ? "morfian_error_network" : "morfian_error_model";
          showToast({
            id: TOAST_IDS.IMAGE_UPLOAD,
            message: t(errorKey),
            variant: "error"
          });
        }
      });
    };
    $[11] = generateMutation;
    $[12] = quota;
    $[13] = t;
    $[14] = tImageUpload;
    $[15] = trimmedDescription;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const runGeneration = t9;
  let t10;
  if ($[17] !== runGeneration) {
    t10 = function handleGenerate() {
      runGeneration(pendingSeedRef.current);
    };
    $[17] = runGeneration;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleGenerate = t10;
  let t11;
  if ($[19] !== runGeneration) {
    t11 = function handleReroll() {
      const next = freshSeed();
      pendingSeedRef.current = next;
      runGeneration(next);
    };
    $[19] = runGeneration;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleReroll = t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = function handleAbort() {
      abortInFlight();
      setState({
        kind: "idle"
      });
    };
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handleAbort = t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = function handleTryAgain() {
      setState({
        kind: "idle"
      });
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleTryAgain = t13;
  let t14;
  if ($[23] !== onConfirm || $[24] !== state.imageUrl || $[25] !== state.kind || $[26] !== state.seed) {
    t14 = function handleConfirm() {
      if (state.kind !== "result") {
        return;
      }
      onEvent("image_generation_used", {
        mode: "morfian"
      });
      onConfirm({
        ipfsUrl: state.imageUrl,
        seed: state.seed
      });
    };
    $[23] = onConfirm;
    $[24] = state.imageUrl;
    $[25] = state.kind;
    $[26] = state.seed;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const handleConfirm = t14;
  if (state.kind === "generating") {
    let t15;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <div className="relative size-48 self-center overflow-hidden rounded-full bg-background-tertiary sm:size-56"><GeneratingFrame shape="circle" expectedDurationMs={12000} done={false}><ImageShimmer /></GeneratingFrame></div>;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    let t16;
    if ($[29] !== tImageUpload) {
      t16 = tImageUpload("cancel");
      $[29] = tImageUpload;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== t16) {
      t17 = <div className="flex flex-col gap-4">{t15}<div className="flex gap-2"><AnimatedButton type="button" variant="secondary" className="min-h-11 flex-1" onClick={handleAbort}>{t16}</AnimatedButton></div></div>;
      $[31] = t16;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    return t17;
  }
  if (state.kind === "result") {
    const t15 = state.imageUrl;
    let t16;
    if ($[33] !== tImageUpload) {
      t16 = tImageUpload("post_crop_preview_alt");
      $[33] = tImageUpload;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    let t17;
    if ($[35] !== resultNsfw.isFlagged || $[36] !== state.imageUrl || $[37] !== t16) {
      t17 = <div className="relative size-48 self-center overflow-hidden rounded-full bg-background-tertiary sm:size-56"><OptimizedImage variant="avatar" src={t15} alt={t16} size={224} className="size-full" imageClassName="object-cover" rounded="rounded-full" nsfw={resultNsfw.isFlagged} preview={false} /></div>;
      $[35] = resultNsfw.isFlagged;
      $[36] = state.imageUrl;
      $[37] = t16;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    let t18;
    if ($[39] !== t) {
      t18 = t("morfian_reroll_cta");
      $[39] = t;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== handleReroll || $[42] !== t18) {
      t19 = <AnimatedButton type="button" variant="secondary" className="min-h-11 flex-1" prefix={RepeatIcon} onClick={handleReroll}>{t18}</AnimatedButton>;
      $[41] = handleReroll;
      $[42] = t18;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== t) {
      t20 = t("confirm");
      $[44] = t;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== handleConfirm || $[47] !== t20) {
      t21 = <AnimatedButton type="button" className="min-h-11 flex-1" onClick={handleConfirm}>{t20}</AnimatedButton>;
      $[46] = handleConfirm;
      $[47] = t20;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] !== t19 || $[50] !== t21) {
      t22 = <div className="flex gap-2">{t19}{t21}</div>;
      $[49] = t19;
      $[50] = t21;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== onCancel || $[53] !== tImageUpload) {
      t23 = onCancel !== undefined ? <AnimatedButton type="button" variant="secondary" size="sm" onClick={onCancel}>{tImageUpload("cancel")}</AnimatedButton> : null;
      $[52] = onCancel;
      $[53] = tImageUpload;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== t17 || $[56] !== t22 || $[57] !== t23) {
      t24 = <div className="flex flex-col gap-4">{t17}{t22}{t23}</div>;
      $[55] = t17;
      $[56] = t22;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    return t24;
  }
  let t15;
  if ($[59] !== state.kind || $[60] !== state.reason || $[61] !== t) {
    t15 = state.kind === "error" ? state.reason === "quota_exceeded" ? t("morfian_quota_exceeded") : state.reason === "nsfw" ? t("morfian_error_nsfw") : state.reason === "network" ? t("morfian_error_network") : t("morfian_error_model") : null;
    $[59] = state.kind;
    $[60] = state.reason;
    $[61] = t;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  const errorMessage = t15;
  let t16;
  if ($[63] !== t) {
    t16 = t("morfian_describe_label");
    $[63] = t;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== t) {
    t17 = t("morfian_describe_placeholder");
    $[65] = t;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = e => {
      setDescription(e.target.value);
    };
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] !== description || $[69] !== t16 || $[70] !== t17) {
    t19 = <TextareaField label={t16} placeholder={t17} value={description} maxLength={MAX_DESCRIPTION_LENGTH} rows={3} onChange={t18} />;
    $[68] = description;
    $[69] = t16;
    $[70] = t17;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  let t20;
  if ($[72] !== errorMessage) {
    t20 = errorMessage !== null ? <Alert variant="destructive" role="alert"><AlertDescription>{errorMessage}</AlertDescription></Alert> : null;
    $[72] = errorMessage;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== onCancel || $[75] !== tImageUpload) {
    t21 = onCancel !== undefined ? <AnimatedButton type="button" variant="secondary" className="min-h-11 flex-1" onClick={onCancel}>{tImageUpload("cancel")}</AnimatedButton> : null;
    $[74] = onCancel;
    $[75] = tImageUpload;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  const t22 = !canGenerate;
  const t23 = state.kind === "error" ? handleTryAgain : handleGenerate;
  let t24;
  if ($[77] !== t) {
    t24 = t("morfian_generate_cta");
    $[77] = t;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t22 || $[80] !== t23 || $[81] !== t24) {
    t25 = <AnimatedButton type="button" className="min-h-11 flex-1" disabled={t22} prefix={SparklesIcon} onClick={t23}>{t24}</AnimatedButton>;
    $[79] = t22;
    $[80] = t23;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== t21 || $[84] !== t25) {
    t26 = <div className="flex gap-2">{t21}{t25}</div>;
    $[83] = t21;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== dailyCap || $[87] !== quotaBlocks || $[88] !== remaining || $[89] !== t) {
    t27 = quotaBlocks ? <p className="text-center text-caption text-foreground-tertiary"><span>{t("morfian_duration_caption", {
          seconds: 12
        })}</span><span> • </span><span className="text-feedback-error-content">{t("morfian_quota_exceeded")}</span></p> : remaining !== null && dailyCap !== null ? <p className="text-center text-caption text-foreground-tertiary"><span>{t("morfian_duration_caption", {
          seconds: 12
        })}</span><span> • </span><span className={cn(remaining <= 2 ? "text-feedback-error-content" : undefined)}>{t("morfian_quota_caption", {
          remaining,
          daily: dailyCap
        })}</span></p> : <p className="text-center text-caption text-foreground-tertiary">{t("morfian_duration_caption", {
        seconds: 12
      })}</p>;
    $[86] = dailyCap;
    $[87] = quotaBlocks;
    $[88] = remaining;
    $[89] = t;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  let t28;
  if ($[91] !== t19 || $[92] !== t20 || $[93] !== t26 || $[94] !== t27) {
    t28 = <div className="flex flex-col gap-4">{t19}{t20}{t26}{t27}</div>;
    $[91] = t19;
    $[92] = t20;
    $[93] = t26;
    $[94] = t27;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  return t28;
}